<template>
  <header :class="{ 'is-expanded': isExpanded }">
    <div class="nav-toggle-button" @click="setNavExpanded(!isExpanded)">
      <FontAwesomeIcon
        class="nav-toggle-button-icon"
        :icon="isExpanded ? 'xmark' : 'bars'"
      />
    </div>

    <div class="logo">
      <a :href="homepageUrl" target="_blank">
        <img src="@/assets/images/skl-logo.png" alt="logo" />
      </a>
    </div>

    <div class="nav-container">
      <AppNavigation
        :is-authenticated="isNavAvailable"
        :is-login-possible="isLoginPossible"
      />
      <LocaleSwitcher />
    </div>
  </header>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import AppNavigation from '@/components/widgets/AppNavigation.vue'
import LocaleSwitcher from '@/components/widgets/LocaleSwitcher.vue'

const route = useRoute()

const isExpanded = ref<boolean>(false)
const isLoginPossible = ref<boolean>(false)
const isNavAvailable = ref<boolean>(false)

const homepageUrl: string =
  process.env.VUE_APP_HOMEPAGE_URL ?? 'https://keilailu.fi/'

const setNavExpanded = (value: boolean): void => {
  isExpanded.value = value
  document.scrollingElement?.scrollTo({ top: 0, behavior: 'smooth' })
  value
    ? document.body.classList.add('no-scroll')
    : document.body.classList.remove('no-scroll')
}

watch(
  () => route.path,
  () => {
    isLoginPossible.value = route.meta.isLoginPossible as boolean
    isNavAvailable.value = route.meta.isNavAvailable as boolean
    setNavExpanded(false)
  },
)
</script>

<style lang="scss" scoped>
@import 'src/assets/scss/globals';

$height-header: 4em;
$height-header-expanded: 100vh;
$height-logo: 2.5em;
$height-toggle-nav-button: 2.5em;

header {
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  height: $height-header;
  max-height: $height-header;
  padding: calc(($height-header - $height-logo) / 2) 1em;
  background-color: $color-accent;
  overflow-y: hidden;
  user-select: none;
  transition: max-height $default-transition;

  @media screen and (min-width: $break-point-laptop) {
    flex-flow: row nowrap;
    align-items: center;
    justify-content: space-between;
  }

  &.is-expanded {
    height: $height-header-expanded;
    max-height: $height-header-expanded;
    overflow-y: scroll;

    @media screen and (min-width: $break-point-laptop) {
      height: $height-header;
      max-height: $height-header;
    }
  }

  .nav-toggle-button {
    position: absolute;
    right: calc(($height-header - $height-toggle-nav-button) / 4);
    top: calc(($height-header - $height-toggle-nav-button) / 2);
    display: flex;
    flex-flow: row nowrap;
    justify-content: center;
    align-items: center;
    width: $height-toggle-nav-button;
    height: $height-toggle-nav-button;
    color: $color-text-inverted;
    cursor: pointer;
    transition: opacity $default-transition;

    @media screen and (min-width: $break-point-laptop) {
      display: none;
    }

    .nav-toggle-button-icon {
      font-size: 1.5em;
    }

    &:hover {
      opacity: 0.7;
    }
  }

  .logo {
    height: $height-logo;

    img {
      max-height: 100%;
      aspect-ratio: 3.6 / 1;
      overflow: hidden;
    }
  }

  .nav-container {
    display: flex;
    flex-flow: column nowrap;
    gap: 1em;
    padding: 2.5em 0;
    list-style: none;

    @media screen and (min-width: $break-point-laptop) {
      flex-flow: row nowrap;
      padding: 0;
    }
  }
}
</style>
